<template>
    <section class="blog">
        <div class="blog__category-filter-container">
            <div class="blog__category-filter-inner">
                <div class="blog__category-filter">
                    <button
                        class="blog__category-text"
                        @click.stop="categoryFilterOpen = !categoryFilterOpen"
                        v-html="currentCategoryName"
                    />
                    <transition name="services__category-buttons">
                        <div v-if="categoryFilterOpen" class="blog__category-buttons">
                            <button
                                class="blog__category-button"
                                @click="selectAllCategories"
                            >All Posts</button>
                            <button
                                v-for="category in categories"
                                :key="category.ID"
                                class="blog__category-button"
                                @click="selectCategory(category)"
                                v-text="decode(category.name)"
                            />
                        </div>
                    </transition>
                </div>
            </div>
        </div>
        <section class="blog__posts-container blog__posts-container--primary">
            <section class="blog__posts">
                <article
                    v-for="(post, index) in posts.slice(0, 5)"
                    v-if="post.post_preview || post.guid"
                    :data-wow-delay="index * 0.15 + 's'"
                    :style="hasLoadedMore ? 'visibility: visible;' : null"
                    class="blog__post wow fadeInUp"
                >
                    <div
                        v-if="post.post_date"
                        v-text="convertDate(post.post_date) + ' / '"
                        class="blog__post-date"
                    />
                    <div
                        v-if="post.date"
                        v-text="convertDate(post.date) + ' / '"
                        class="blog__post-date"/>
                    <div
                        v-if="post.category_list"
                        v-text="getCategoryNames(post.category_list)"
                        class="blog__post-category"
                    />
                    <div
                        v-if="post.categories"
                        v-text="getCategoryNames(post.categories)"
                        class="blog__post-category"
                    />
                    <a
                        v-if="post.post_title"
                        v-text="post.post_title"
                        :href="post.slug"
                        class="blog__post-heading"
                    />
                    <a
                        v-if="post.title"
                        v-text="decode(post.title.rendered)"
                        :href="post.slug"
                        class="blog__post-heading"
                    />
                    <div
                        v-if="post.post_preview"
                        v-text="spliceContent(post.post_preview)"
                        class="blog__post-content"
                    />
                    <div
                        v-else
                        v-text="spliceContent(post.acf.post_preview)"
                        class="blog__post-content"
                    />
                    <a
                        v-if="post.slug"
                        :href="post.slug"
                        class="blog__post-link"
                    >
                        Read More
                    </a>
                    <hr class="blog__post-hr">
                </article>
            </section>
            <div
                data-wow-delay="0.25s"
                ref="featuredPost"
                class="blog__post blog__post--featured"
            >
                <slot name="featured-post--primary"/>
            </div>
        </section>
        <slot/>
        <section class="blog__posts-container blog__posts-container--secondary">
            <section class="blog__posts blog__posts--secondary">
                <article
                    v-for="(post, index) in posts.slice(5)"
                    v-if="post.post_preview || post.guid"
                    :data-wow-delay="index * 0.15 + 's'"
                    :style="hasLoadedMore ? 'visibility: visible;' : null"
                    class="blog__post wow fadeInUp"
                >
                    <div
                        v-if="post.post_date"
                        v-text="convertDate(post.post_date)"
                        class="blog__post-date"
                    />
                    <div
                        v-if="post.date"
                        v-text="convertDate(post.date)"
                        class="blog__post-date"
                    />
                    <div
                        v-if="post.category_list"
                        v-text="getCategoryNames(post.category_list)"
                        class="blog__post-category"
                    />
                    <div
                        v-if="post.categories"
                        v-text="getCategoryNames(post.categories)"
                        class="blog__post-category"
                    />
                    <a
                        v-if="post.post_title"
                        v-text="post.post_title"
                        :href="post.slug"
                        class="blog__post-heading"
                    />
                    <a
                        v-if="post.title"
                        v-text="decode(post.title.rendered)"
                        :href="post.slug"
                        class="blog__post-heading"
                    />
                    <div
                        v-if="post.post_preview"
                        v-text="spliceContent(post.post_preview)"
                        class="blog__post-content"
                    />
                    <div
                        v-else
                        v-text="spliceContent(post.acf.post_preview)"
                        class="blog__post-content"
                    />
                    <a
                        v-if="post.slug"
                        :href="post.slug"
                        class="blog__post-link"
                        v-text="'Read More'"
                    />
                    <hr class="blog__post-hr">
                </article>
            </section>
            <div class="blog__post blog__post-featured_secondary">
                <slot name="featured-post--secondary"/>
            </div>
        </section>
        <div
            v-if="showLoadMore"
            class="blog__load-more-container"
        >
            <button
                @click="getPosts(false)"
                class="blog__load-more"
                v-text="buttonText"
            />
        </div>
    </section>
</template>

<script>
import he from 'he';
import axios from 'axios';
import moment from 'moment';

export default {
    name: 'BrandBlog',
    props: {
        buttonText: {
            type: String,
            required: true,
        },
        initialPosts: {
            type: Array,
            required: true,
        },
        categories: {
            type: Array,
            required: true,
        },
        featuredPosts: {
            type: Array,
        },
    },
    data() {
        return {
            currentPage: 1,
            posts: this.initialPosts,
            currentCategory: undefined,
            previousCategory: undefined,
            categoryFilterOpen: false,
            hasLoadedMore: false,
            showLoadMore: true,
            isSticky: false,
        };
    },
    computed: {
        categoryIDs() {
            const IDList = [];
            this.categories.forEach((category) => {
                IDList.push(category.ID);
            });
            return IDList;
        },
        currentCategoryName() {
            if (!this.currentCategory) {
                return null;
            }

            if (this.currentCategory instanceof Array) {
                return 'All Posts';
            }

            const { categories, currentCategory } = this;
            const { length } = categories;

            for (let index = 0; index < length; index += 1) {
                const category = categories[index];

                if (category.ID === currentCategory) {
                    return category.name;
                }
            }

            return null;
        },
    },
    /* eslint-disable */
    methods: {
        intersect(entries) {
            const entry = entries.filter(({target}) => {
                return target === this.$refs.featuredPost;
            });

            if (entry[0].intersectionRatio < 0.1) {
                this.isSticky = true;
            } else {
                this.isSticky = false;
            }
        },
        decode(str) {
            return he.decode(str);
        },
        spliceContent(postContent) {
            const spliced = postContent;
            if (postContent) {
                return spliced
                    .split(' ')
                    .splice(0, 50)
                    .join(' ')
                    .concat('...')
                ;
            }
        },
        getPosts(resetPageCount) {
            if (!this.hasLoadedMore) {
                this.hasLoadedMore = true;
            }

            if (resetPageCount === true) {
                this.currentPage = 1;
                this.showLoadMore = true;
            } else {
                this.currentPage += 1;
            }

            axios
                .get('/wp-json/wp/v2/blog', {
                    params: {
                        page: this.currentPage,
                        per_page: 12,
                        categories: this.currentCategory.toString(),
                    },
                })
                .then((response) => {
                    if (resetPageCount) {
                        this.posts = response.data;
                    } else {
                        this.posts.push(...response.data);
                    }

                    if (response.data.length < 12) {
                        this.showLoadMore = false;
                    }
                })
                .catch((error) => {
                    this.showLoadMore = false;
                });
        },
        getCategoryNames(categories) {
            if (categories) {
                const categoryNameList = [];
                categories.forEach((currentCategory) => {
                    this.categories.forEach((category) => {
                        if (currentCategory === category.ID) {
                            categoryNameList.push(this.decode(category.name));
                        }
                    });
                });

                return categoryNameList.join(', ');
            }

            return null;
        },
        convertDate(date) {
            return moment(date, 'YYYYMMDD').format('LL');
        },
        selectAllCategories() {
            this.currentCategory = this.categoryIDs;
            this.categoryFilterOpen = false;

            this.getPosts(true);
        },
        selectCategory({ id }) {
            this.currentCategory = id;
            this.categoryFilterOpen = false;

            this.getPosts(true);
        },
        handleStickyPost(container, articleHeight, headerHeight) {
            Array.prototype.forEach.call(container, (el, index)  => {
                addEventListener('scroll', () => {
                    const containerElement = el.getBoundingClientRect();
                    if ((containerElement.height + containerElement.top - articleHeight[index].getBoundingClientRect().height) < headerHeight) {
                        el.classList.remove('sticky');
                        el.classList.add('postStatic');
                    } else if(containerElement.top < headerHeight) {
                        el.classList.remove('postStatic');
                        el.classList.add('sticky');
                    } else {
                        el.classList.remove('sticky');
                        el.classList.remove('postStatic');
                    }
                });
            })
        },
    },
    created() {
        this.currentCategory = this.categoryIDs;
        this.previousCategory = this.categoryIDs;
    },
    mounted() {
        if (this.initialPosts.length < 12) {
            this.showLoadMore = false;
        };

        addEventListener('click', () => { this.categoryFilterOpen = false; });

        const headerHeight = 67;
        const postsContainer = document.querySelectorAll('.blog__posts-container');
        const articleContainer = document.querySelectorAll('.featured-article');

        this.handleStickyPost(postsContainer, articleContainer, headerHeight)
    },
};
</script>



// WEBPACK FOOTER //
// BrandBlog.vue?ece61842